import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../context/AuthContext';

function AppUsers() {
  const { currentUser } = useAuth();
  const [appUsers, setAppUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAppUser, setCurrentAppUser] = useState({ 
    username: '', 
    email: '', 
    phone: '', 
    merchant_id: '',
    status: 'active' // Added status default value
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [toggleLoading, setToggleLoading] = useState({});

  // Check if current user is admin (to show filter) or merchant (to auto-filter)
  const isAdmin = currentUser?.role === 'admin';
  const isMerchant = currentUser?.role === 'merchant';
  const userMerchantId = currentUser?.merchant_id;

  const fetchMerchants = async () => {
    try {
      const response = await axios.get('/admin/merchants');
      setMerchants(response.data.merchants || []);
    } catch (err) {
      console.error('Error fetching merchants:', err);
    }
  };

  const fetchAppUsers = async () => {
    try {
      const response = await axios.get('/admin/app-users');
      const users = response.data.appUsers || [];
      setAppUsers(users);
      
      // Auto-filter for merchants
      if (isMerchant && userMerchantId) {
        const merchantUsers = users.filter(user => user.merchant_id === userMerchantId);
        setFilteredUsers(merchantUsers);
      } else {
        setFilteredUsers(users);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load app users');
      setLoading(false);
      console.error('App users fetch error:', err);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchMerchants(); // Only fetch merchants for admin
    }
    fetchAppUsers();
  }, [currentUser]);

  // Filter users by merchant (only for admin)
  useEffect(() => {
    if (isAdmin) {
      if (selectedMerchant === '') {
        setFilteredUsers(appUsers);
      } else {
        const filtered = appUsers.filter(user => 
          user.merchant_id === selectedMerchant
        );
        setFilteredUsers(filtered);
      }
    }
  }, [selectedMerchant, appUsers, isAdmin]);

  const handleMerchantFilter = (event) => {
    setSelectedMerchant(event.target.value);
  };

  const clearFilter = () => {
    setSelectedMerchant('');
  };

  const handleOpenDialog = (appUser = null) => {
    setErrorMessage(''); // Clear error message when opening the modal
    if (appUser) {
      setEditMode(true);
      setCurrentAppUser(appUser);
    } else {
      setEditMode(false);
      // Auto-set merchant_id for merchant role users
      const initialUser = { username: '', email: '', phone: '', merchant_id: '' };
      if (isMerchant && userMerchantId) {
        initialUser.merchant_id = userMerchantId;
      }
      setCurrentAppUser(initialUser);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentAppUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    console.log("Submitting user:", currentAppUser);
    try {
      // For merchant role, ensure merchant_id is set to their own merchant_id
      const userToSave = { ...currentAppUser };
      if (isMerchant && userMerchantId) {
        userToSave.merchant_id = userMerchantId;
      }

      if (editMode) {
        await axios.put(`/admin/app-users/${currentAppUser.id}`, userToSave);
      } else {
        await axios.post('/admin/app-users', userToSave);
      }
      fetchAppUsers();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving app user:', err);
      setErrorMessage(err.response?.data?.message || 'Failed to save app user.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this app user?')) {
      try {
        await axios.delete(`/admin/app-users/${id}`);
        fetchAppUsers();
      } catch (err) {
        console.error('Error deleting app user:', err);
        alert('Failed to delete app user.');
      }
    }
  };

  // New function to toggle user status
  const handleToggleStatus = async (userId, currentStatus) => {
    // Prevent multiple clicks
    if (toggleLoading[userId]) return;
    
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    
    setToggleLoading(prev => ({ ...prev, [userId]: true }));
    
    try {
      await axios.put(`/admin/app-users/toggle-status/${userId}`, { status: newStatus });
      
      // Update local state
      const updatedUsers = appUsers.map(user => {
        if (user.id === userId) {
          return { ...user, status: newStatus };
        }
        return user;
      });
      
      setAppUsers(updatedUsers);
      
      // Update filtered users as well
      const updatedFilteredUsers = filteredUsers.map(user => {
        if (user.id === userId) {
          return { ...user, status: newStatus };
        }
        return user;
      });
      
      setFilteredUsers(updatedFilteredUsers);
      
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert('Failed to update user status');
    } finally {
      setToggleLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const getMerchantName = (merchant_id, merchant_name) => {
    // Use merchant_name from the API response if available
    if (merchant_name) {
      return merchant_name;
    }
    // Fallback to looking up in merchants array (for backward compatibility)
    const merchant = merchants.find(m => String(m.merchant_id) === String(merchant_id));
    return merchant ? merchant.name : 'Unknown Merchant';
  };

  // Get status chip based on user status
  const getStatusChip = (status) => {
    switch (status) {
      case 'active':
        return (
          <Chip 
            icon={<CheckCircleIcon fontSize="small" />}
            label="Active"
            size="small"
            color="success"
          />
        );
      case 'suspended':
        return (
          <Chip 
            icon={<BlockIcon fontSize="small" />}
            label="Suspended"
            size="small"
            color="error"
          />
        );
      case 'inactive':
        return (
          <Chip 
            label="Inactive"
            size="small"
            color="default"
          />
        );
      default:
        return (
          <Chip 
            label={status || "Unknown"}
            size="small"
            color="default"
          />
        );
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          App Users {isMerchant && currentUser?.merchant_name ? `(${currentUser.merchant_name})` : ''}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Add New App User
        </Button>
      </Box>

      {/* Filter Section - Only show for admin */}
      {isAdmin && (
        <Box display="flex" alignItems="center" gap={2} mb={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <FilterListIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Merchant</InputLabel>
            <Select
              value={selectedMerchant}
              label="Filter by Merchant"
              onChange={handleMerchantFilter}
            >
              <MenuItem value="">All Merchants</MenuItem>
              {merchants.map((merchant) => (
                <MenuItem key={merchant.merchant_id} value={String(merchant.merchant_id)}>
                  {merchant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedMerchant && (
            <Chip 
              label={`Filtered: ${getMerchantName(selectedMerchant, null)}`}
              onDelete={clearFilter}
              color="primary"
              variant="outlined"
            />
          )}
          <Typography variant="body2" color="textSecondary">
            Showing {filteredUsers.length} of {appUsers.length} users
          </Typography>
        </Box>
      )}

      {/* Show count info for merchants */}
      {isMerchant && (
        <Box mb={3} p={2} sx={{ backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredUsers.length} users for your merchant account
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="app users table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Balance</TableCell>
              {/* Only show Merchant column for admin */}
              {isAdmin && <TableCell>Merchant</TableCell>}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((appUser) => (
              <TableRow key={appUser.id}>
                <TableCell>{appUser.id}</TableCell>
                <TableCell>{appUser.username}</TableCell>
                <TableCell>{appUser.email}</TableCell>
                <TableCell>{appUser.phone || 'N/A'}</TableCell>
                <TableCell>
                  {getStatusChip(appUser.status)}
                </TableCell>
                <TableCell>¥{parseFloat(appUser.deposit_balance || 0).toFixed(0)}</TableCell>
                {/* Only show Merchant cell for admin */}
                {isAdmin && (
                  <TableCell>
                    <Chip 
                      label={getMerchantName(appUser.merchant_id, appUser.merchant_name)}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <IconButton color="primary" onClick={() => handleOpenDialog(appUser)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(appUser.id)}>
                      <DeleteIcon />
                    </IconButton>
                    <Tooltip title={appUser.status === 'active' ? 'Suspend User' : 'Activate User'}>
                      <Switch
                        checked={appUser.status === 'active'}
                        onChange={() => handleToggleStatus(appUser.id, appUser.status)}
                        color="primary"
                        size="small"
                        disabled={toggleLoading[appUser.id]}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editMode ? 'Edit App User' : 'Add New App User'}</DialogTitle>
        <DialogContent>
          {errorMessage && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Typography>
          )}
          <TextField
            margin="dense"
            name="username"
            label="Name (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={currentAppUser.username || ''}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email Address *"
            type="email"
            fullWidth
            required
            variant="outlined"
            value={currentAppUser.email}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="phone"
            label="Phone Number *"
            type="text"
            fullWidth
            required
            variant="outlined"
            value={currentAppUser.phone || ''}
            onChange={handleChange}
          />
          
          {/* Add status selection */}
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel>Status *</InputLabel>
            <Select
              name="status"
              label="Status *"
              value={currentAppUser.status || 'active'}
              onChange={handleChange}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          
          {/* Merchant selection - Only show for admin role */}
          {isAdmin && (
            <FormControl fullWidth margin="dense" variant="outlined" required>
              <InputLabel>Merchant *</InputLabel>
              <Select
                name="merchant_id"
                label="Merchant *"
                value={currentAppUser.merchant_id !== '' ? String(currentAppUser.merchant_id) : ''}
                onChange={handleChange}
              >
                {merchants.map((merchant) => (
                  <MenuItem key={merchant.merchant_id} value={String(merchant.merchant_id)}>
                    {merchant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AppUsers;

